import React, { ButtonHTMLAttributes } from 'react';
import classNames from 'classnames';
import { FCP } from 'types';

import classes from './index.module.css';

enum Aligns {
  LEFT = 'start',
  RIGHT = 'end',
  CENTER = 'center',
}

export interface ZpButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, FCP {
  theme?: 'primary' | 'secondary' | 'super-primary' | 'link' | 'danger';
  size?: 'big' | 'small';
  type?: 'button' | 'submit' | 'reset';
  staticWidth?: null | string | number;
  minWidth?: number | string;
  icon?: React.ReactNode;
  isLeftIcon?: boolean;
  dropdownStatus?: null | '' | 'open' | 'closed';
  isFullWidth?: boolean;
  isAutoWidth?: boolean;
  textNowrap?: boolean;
  overflowHidden?: boolean;
  align?: `${Aligns}`;
  withContent?: boolean;
}

const ZpButton = ({
  theme = 'primary',
  children,
  size = 'small',
  staticWidth = null,
  minWidth = null,
  icon = null,
  isLeftIcon = true,
  dropdownStatus = null,
  className,
  isFullWidth = false,
  isAutoWidth = false,
  textNowrap = false,
  align = 'center',
  withContent = true,
  overflowHidden = false,
  ...restButtonConfig
}: ZpButtonProps) => {
  const combinedClasses = classNames(
    classes.button,
    classes[theme],
    classes[size],
    {
      [classes['btn-link']]: theme === 'link',
      [classes['with-icon']]: icon,
      [classes['with-right-icon']]: (icon || dropdownStatus) && (!isLeftIcon || dropdownStatus),
      [classes['with-left-icon']]: (icon || dropdownStatus) && isLeftIcon && !dropdownStatus,
      [classes['btn-dropdown']]: dropdownStatus,
      [classes['dropdown-open']]: dropdownStatus === 'open',
      [classes['full-width']]: isFullWidth,
      [classes['auto-width']]: isAutoWidth,
      'text-left': align === Aligns.LEFT,
      'text-right': align === Aligns.RIGHT,
    },
    className
  );

  return (
    <button
      data-testid="zp-button"
      type="button"
      style={{ width: staticWidth, minWidth: minWidth || staticWidth, maxWidth: staticWidth }}
      className={combinedClasses}
      {...restButtonConfig}
    >
      <div
        className={classNames(classes.content, {
          [classes['dropdown-content']]: dropdownStatus,
          'justify-content-start': align === Aligns.LEFT,
          'justify-content-end': align === Aligns.RIGHT,
        })}
      >
        {isLeftIcon && icon && (
          <div
            data-testid="with-left-icon"
            className={`${classes.content} ${classes.icon} ${classes['icon-left']}`}
          >
            {icon}
          </div>
        )}
        {withContent && (
          <div
            data-testid="with-content"
            className={classNames(
              classes.text,
              { 'text-nowrap': textNowrap },
              { 'overflow-hidden': overflowHidden },
              { [classes['pull-icon-right']]: dropdownStatus }
            )}
          >
            {children}
          </div>
        )}
        {!isLeftIcon && icon && (
          <div
            data-testid="with-right-icon"
            className={`${classes.content} ${classes.icon} ${classes['icon-right']}`}
          >
            {icon}
          </div>
        )}
        {!withContent && children}
      </div>
    </button>
  );
};

export default ZpButton;
